// Extração por regras da ficha de acompanhamento da obra (FAO, .docx). Código puro, compartilhado com o app.
//
// As FAOs não seguem um modelo único, mas todas se organizam por títulos em negrito: partes ("I) Informações
// gerais", "A obra", "III) Sobre o autor", "A edição", "EXTRA) Links…") e, dentro delas, seções ("Sinopse",
// "Contexto", "Temas principais", "Diferenciais"…). Cada seção reconhecida alimenta um campo da FAO. Um título
// não reconhecido continua no campo quando ele é uma lista (ex.: os grupos de "Principais obras"); no texto
// corrido, abre um assunto novo no campo livre da parte (contexto, na obra; biografia, no autor). As
// informações gerais (título original, autoria, equipe) ficam de fora: vêm dos dados editoriais e dos
// créditos. Conteúdo de outro livro no mesmo arquivo (livreto do box, outra FAO) é ignorado.

import type { DocxParagraph } from "./credits-extraction.ts"

export interface ExtractedFao {
  synopsis: string
  context: string
  mainThemes: string
  curiosities: string
  authorBiography: string
  mainWorks: string
  editionHighlights: string
  referenceLinks: string
}

export type ExtractedFaoField = keyof ExtractedFao

type Part = "HEADER" | "GENERAL" | "WORK" | "AUTHOR" | "EDITION" | "LINKS"

const partDefaults: Record<Part, ExtractedFaoField | null> = { HEADER: null, GENERAL: null, WORK: "synopsis", AUTHOR: "authorBiography", EDITION: "editionHighlights", LINKS: "referenceLinks" }

/** Campo de um título não reconhecido no meio de um texto corrido. */
const unknownHeadingFields: Record<Part, ExtractedFaoField | null> = { HEADER: null, GENERAL: null, WORK: "context", AUTHOR: "authorBiography", EDITION: "editionHighlights", LINKS: "referenceLinks" }

const sectionRules: Array<[RegExp, ExtractedFaoField | null]> = [
  [/^sinopse\b/, "synopsis"],
  [/^(contexto|significado|legado)\b/, "context"],
  [/^temas?\b/, "mainThemes"],
  [/^(curiosidades?|outras informacoes)\b/, "curiosities"],
  [/^(breve )?biografia\b/, "authorBiography"],
  [/^(principais )?obras\b/, "mainWorks"],
  [/^(links?|materiais|referencias|bibliografia|conteudos)\b/, "referenceLinks"],
  [/^(diferenciais|sobre a edicao)\b/, "editionHighlights"],
]

/** Título que abre outro livro dentro do mesmo arquivo; "Livro principal" marca o próprio livro. */
const otherBookPattern = /^(livreto|livro (secundario|de apoio|complementar|\d)|volume \d|abc d[aeo]s?\b)/
const mainBookPattern = /^livro principal\b/
const faoTitlePattern = /^ficha de acompanhamento/

const maxHeadingLength = 120

function normalize(value: string) {
  return value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim()
}

/** Parte do documento aberta por este título ("II) Informações de contexto…", "O(a) autor(a)"…), se for o caso. */
function partOf(heading: string): Part | null {
  const normalized = normalize(heading)
  const numbered = /^([ivx]+|extra)\s*[).:-]/.test(normalized)
  const named = /^(informacoes gerais|a obra|sobre (o|a) autor|o\(a\) autor|(o|a) autor(a)?$|a edicao|sobre a edicao|links\b)/.test(normalized)
  if (!numbered && !named) return null
  const text = normalized.replace(/^([ivx]+|extra)\s*[).:-]\s*/, "")
  if (/autor/.test(text)) return "AUTHOR"
  if (/^informacoes gerais/.test(text)) return "GENERAL"
  if (/^(links|conteudos|referencias)/.test(text) || /^extra/.test(normalized)) return "LINKS"
  if (/^(a|sobre a) edicao/.test(text)) return "EDITION"
  return "WORK"
}

function sectionOf(heading: string): { field: ExtractedFaoField | null } | null {
  const normalized = normalize(heading)
  const rule = sectionRules.find(([pattern]) => pattern.test(normalized))
  return rule ? { field: rule[1] } : null
}

function isHeading(paragraph: DocxParagraph) {
  return paragraph.bold && paragraph.text.length <= maxHeadingLength && !/^https?:\/\//i.test(paragraph.text)
}

type Block = { text: string; kind: "heading" | "text" | "list" }

/** Campos em forma de lista: um item por linha, com linha em branco só antes de um subtítulo. */
const listFields = new Set<ExtractedFaoField>(["mainThemes", "mainWorks", "editionHighlights", "referenceLinks"])

/**
 * Texto do campo. Nos campos de texto corrido, parágrafos separados por linha em branco; nos de lista, um
 * item por linha. Itens de lista do Word levam "- "; subtítulos ficam numa linha própria, com linha em branco antes.
 */
function joinBlocks(field: ExtractedFaoField, blocks: Block[]) {
  let result = ""
  let previous: Block["kind"] | null = null
  for (const block of blocks) {
    if (result) {
      const tight = block.kind !== "heading" && previous !== null && (listFields.has(field) || (block.kind === "list" && previous === "list"))
      result += tight ? "\n" : "\n\n"
    }
    result += block.kind === "list" ? `- ${block.text}` : block.text
    previous = block.kind
  }
  return result
}

export function parseFaoParagraphs(paragraphs: DocxParagraph[]): ExtractedFao {
  const blocks: Record<ExtractedFaoField, Block[]> = { synopsis: [], context: [], mainThemes: [], curiosities: [], authorBiography: [], mainWorks: [], editionHighlights: [], referenceLinks: [] }
  let part: Part = "HEADER"
  let field: ExtractedFaoField | null = null
  let skippingOtherBook = false
  let seenFaoTitle = false

  for (const paragraph of paragraphs) {
    if (!paragraph.text) continue
    if (isHeading(paragraph)) {
      const normalized = normalize(paragraph.text)
      if (faoTitlePattern.test(normalized)) {
        // Uma segunda FAO no mesmo arquivo é de outro livro: o resto é ignorado.
        if (seenFaoTitle) break
        seenFaoTitle = true
        continue
      }
      if (mainBookPattern.test(normalized)) {
        skippingOtherBook = false
        continue
      }
      if (otherBookPattern.test(normalized)) {
        skippingOtherBook = true
        continue
      }
      const nextPart = partOf(paragraph.text)
      if (nextPart) {
        part = nextPart
        field = partDefaults[part]
        skippingOtherBook = false
        continue
      }
      if (skippingOtherBook || part === "HEADER" || part === "GENERAL") continue
      // Nos links, os títulos são dos vídeos e textos indicados: nunca mudam de campo.
      const section = part === "LINKS" ? null : sectionOf(paragraph.text)
      if (section) {
        field = section.field
        // "Sinopse: texto…" na mesma linha: o que vem depois dos dois-pontos já é conteúdo.
        const inline = paragraph.text.replace(/^[^:]*:\s*/, "")
        if (field && inline !== paragraph.text && inline) blocks[field].push({ text: inline, kind: "text" })
        continue
      }
      // Título não reconhecido: nas listas é um grupo ("Textos técnicos" em "Principais obras") e fica no
      // campo; no texto corrido abre um assunto novo, que vai para o campo de texto livre da parte.
      if (!field || !listFields.has(field)) field = unknownHeadingFields[part]
      if (field) blocks[field].push({ text: paragraph.text, kind: "heading" })
      continue
    }
    if (skippingOtherBook || !field) continue
    blocks[field].push({ text: paragraph.text, kind: paragraph.list ? "list" : "text" })
  }

  return {
    synopsis: joinBlocks("synopsis", blocks.synopsis),
    context: joinBlocks("context", blocks.context),
    mainThemes: joinBlocks("mainThemes", blocks.mainThemes),
    curiosities: joinBlocks("curiosities", blocks.curiosities),
    authorBiography: joinBlocks("authorBiography", blocks.authorBiography),
    mainWorks: joinBlocks("mainWorks", blocks.mainWorks),
    editionHighlights: joinBlocks("editionHighlights", blocks.editionHighlights),
    referenceLinks: joinBlocks("referenceLinks", blocks.referenceLinks),
  }
}

export function extractedFaoFields(fao: ExtractedFao): ExtractedFaoField[] {
  return (Object.keys(fao) as ExtractedFaoField[]).filter((field) => Boolean(fao[field]))
}
